import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {Warning} from '../../../icons';
import {AGENCY_ACCOUNTS_PATH} from '../../../layout/v2/agencyPages';
import {
    NS,
    addonPriceText,
    downgradeOverage,
    downgradeUsers,
    formatAmount,
    listMonthly,
} from './subscriptionPageModel';

const RAW = {escapeValue: false};

/**
 * La confirmación antes de abrir el chat (mockup 3). El «Listo, lo recibimos» del mockup se reemplazó por
 * el chat (copy.md §8): el botón principal abre el chat con el pedido escrito y la persona lo envía ahí.
 *
 * `request.kind`: `'plan'` (subir o bajar, con la tabla hoy / nuevo) o `'annual'`.
 */
export function RequestConfirmModal({request, currentName, currentPlan, price, usage, onClose, onSend}) {
    const {t} = useTranslation();
    const [comment, setComment] = useState('');
    if (!request) return null;

    let title;
    let body;
    if (request.kind === 'annual') {
        title = t(`${NS}.confirm.annual_title`);
        body = <AnnualBody offer={request.offer} planName={currentName}/>;
    } else {
        const name = t(request.plan.nameKey);
        title = request.direction === 'down'
            ? t(`${NS}.confirm.downgrade_title`, {plan: name, interpolation: RAW})
            : t(`${NS}.confirm.upgrade_title`, {plan: name, interpolation: RAW});
        body = <PlanBody request={request} currentPlan={currentPlan} price={price} usage={usage}
                         comment={comment} onComment={setComment}/>;
    }

    const isDowngradeOver = request.kind === 'plan' && request.direction === 'down'
        && !!downgradeOverage(request.plan, usage);

    return (
        <Modal isOpen toggle={onClose} className="sp-modal" labelledBy="sp-confirm-title" scrollable>
            <ModalHeader toggle={onClose} tag="h2" id="sp-confirm-title">{title}</ModalHeader>
            <ModalBody>{body}</ModalBody>
            <ModalFooter>
                {isDowngradeOver ? (
                    <Link to={AGENCY_ACCOUNTS_PATH} className="btn -alt -negative" onClick={onClose}>
                        {t(`${NS}.confirm.see_accounts`)}
                    </Link>
                ) : (
                    <button type="button" className="btn -alt -negative" onClick={onClose}>
                        {t(`${NS}.confirm.back`)}
                    </button>
                )}
                <button type="button" className={isDowngradeOver ? 'btn -alt -negative' : 'btn -alt'}
                        onClick={() => onSend(comment)}>
                    {isDowngradeOver ? t(`${NS}.confirm.send_anyway`) : t(`${NS}.confirm.send`)}
                </button>
            </ModalFooter>
        </Modal>
    );
}

function AnnualBody({offer, planName}) {
    const {t, i18n} = useTranslation();
    const lang = i18n && i18n.language;
    return (
        <p className="sp-modal__text">
            {t(`${NS}.confirm.annual_body`, {
                plan: planName,
                price: formatAmount(offer.monthly, offer.currency, lang),
                total: formatAmount(offer.total, offer.currency, lang),
                savings: formatAmount(offer.savings, offer.currency, lang),
                interpolation: RAW,
            })}
        </p>
    );
}

/**
 * Subir o bajar: la tabla «Tu plan hoy / Con X». La primera columna son los topes REALES de la agencia
 * (`usage`) y lo que paga (`price`); sin el dato, «—». Los usuarios van con el número del plan, que es lo
 * único que hay (D-6). Se rotula «Tu plan hoy» y no «Hoy»: con el aviso de arriba («Usas 64…») al lado, «Hoy
 * 70» se leía como uso (QA-VIS-spfe-80).
 */
function PlanBody({request, currentPlan, price, usage, comment, onComment}) {
    const {t, i18n} = useTranslation();
    const lang = i18n && i18n.language;
    const {plan, direction, period, currency} = request;
    const name = t(plan.nameKey);
    const dash = '—';
    const accountsToday = usage && usage.ad_accounts && typeof usage.ad_accounts.limit === 'number'
        ? usage.ad_accounts.limit : dash;
    const portalToday = usage && usage.portal_accesses && typeof usage.portal_accesses.limit === 'number'
        ? usage.portal_accesses.limit : dash;
    // En la celda va «Ilimitados» a secas: el rótulo de la fila ya dice «Usuarios del equipo».
    let usersToday = dash;
    if (currentPlan) {
        usersToday = currentPlan.quantities.teamUsers === null
            ? t(`${NS}.confirm.unlimited`) : currentPlan.quantities.teamUsers;
    }
    const usersNew = plan.quantities.teamUsers === null ? t(`${NS}.confirm.unlimited`) : plan.quantities.teamUsers;
    const priceToday = price ? formatAmount(price.monthlyEquivalent, price.currency, lang) : dash;
    const priceNew = formatAmount(listMonthly(plan.id, currency, period), currency, lang) || dash;

    const overage = direction === 'down' ? downgradeOverage(plan, usage) : null;
    const extraUsers = direction === 'down' ? downgradeUsers(plan, usage) : null;
    const premium = plan.premiumSupport && !(currentPlan && currentPlan.premiumSupport);

    return (
        <div className="sp-modal__body">
            {overage && (
                <div className="sp-warnbox" role="note">
                    <Warning size={20} weight="bold" aria-hidden="true"/>
                    <div>
                        <p className="sp-warnbox__title">
                            {t(`${NS}.confirm.downgrade_over_title`, {plan: name, interpolation: RAW})}
                        </p>
                        <p>
                            {t(`${NS}.confirm.downgrade_over_body`, {
                                count: overage.blocks, used: overage.used, limit: overage.limit, plan: name,
                                price: addonPriceText(t, 'accounts', {currency, lang, times: overage.blocks}),
                                interpolation: RAW,
                            })}
                        </p>
                        {extraUsers && (
                            <p>{t(`${NS}.confirm.downgrade_users`, {
                                used: extraUsers.used, limit: extraUsers.limit, plan: name, interpolation: RAW,
                            })}</p>
                        )}
                    </div>
                </div>
            )}
            {!overage && extraUsers && (
                <p className="sp-modal__note">{t(`${NS}.confirm.downgrade_users`, {
                    used: extraUsers.used, limit: extraUsers.limit, plan: name, interpolation: RAW,
                })}</p>
            )}
            <table className="sp-compare">
                <thead>
                    <tr>
                        <th scope="col"><span className="visually-hidden">{t(`${NS}.plans.title`)}</span></th>
                        <th scope="col">{t(`${NS}.confirm.today`)}</th>
                        <th scope="col" className="-new">{t(`${NS}.confirm.with_plan`, {plan: name, interpolation: RAW})}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr><th scope="row">{t(`${NS}.confirm.row_accounts`)}</th><td>{accountsToday}</td><td className="-new">{plan.quantities.linkedAccounts}</td></tr>
                    <tr><th scope="row">{t(`${NS}.confirm.row_users`)}</th><td>{usersToday}</td><td className="-new">{usersNew}</td></tr>
                    <tr><th scope="row">{t(`${NS}.confirm.row_portal`)}</th><td>{portalToday}</td><td className="-new">{plan.quantities.portalAccesses}</td></tr>
                    <tr><th scope="row">{t(`${NS}.confirm.row_price`)}</th><td>{priceToday}</td><td className="-new">{priceNew}</td></tr>
                </tbody>
            </table>
            <p className="sp-modal__note">
                {direction === 'down'
                    ? t(`${NS}.confirm.downgrade_note`)
                    : <>{premium && `${t(`${NS}.confirm.premium_note`)} `}{t(`${NS}.confirm.upgrade_note`)}</>}
            </p>
            <div className="sp-modal__field">
                <label htmlFor="sp-confirm-comment">
                    {t(`${NS}.confirm.comment_label`)} <span className="sp-muted">{t(`${NS}.confirm.optional`)}</span>
                </label>
                <textarea id="sp-confirm-comment" className="sp-input" rows={3} value={comment}
                          placeholder={t(`${NS}.confirm.comment_placeholder`)}
                          onChange={(event) => onComment(event.target.value)}/>
            </div>
        </div>
    );
}
